import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  CalendarDays, MapPin, Users, Share2, Edit3, Plus,
  Check, X, Clock, ArrowRight, Trash2, Mail, Phone,
  Heart, Info, PartyPopper, UserCheck, UserX, HelpCircle,
  Send, QrCode, Bell, Download, RefreshCw, MessageSquare,
  Sparkles, FileText, CreditCard, Image, Zap, ChevronDown, ChevronUp
} from 'lucide-react';
import { formatDate, formatDateWithDay } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const IA_MODES = {
  createur: {
    label: 'Créateur',
    emoji: '✨',
    color: '#8B5CF6',
    bg: 'rgba(139,92,246,0.08)',
    desc: 'Thème, concept, idées créatives, couleurs, animations recommandées',
  },
  organisateur: {
    label: 'Organisateur',
    emoji: '📋',
    color: '#5DBCBF',
    bg: 'var(--teal-mist)',
    desc: 'Checklist complète, points critiques, conseils budget, erreurs à éviter',
  },
  planificateur: {
    label: 'Planificateur',
    emoji: '⏱️',
    color: '#D4AF37',
    bg: 'rgba(212,175,55,0.08)',
    desc: 'Planning jour J, actions avant, timing clé, contacts urgence',
  },
};

const EVENT_TYPES = [
  { id: 'anniversaire_enfant', label: 'Anniversaire enfant', icon: '🎂', color: '#F4A7B2' },
  { id: 'anniversaire_adulte', label: 'Anniversaire adulte', icon: '🥂', color: '#D4AF37' },
  { id: 'mariage', label: 'Mariage', icon: '💍', color: '#5DBCBF' },
  { id: 'entreprise', label: 'Entreprise / CE', icon: '💼', color: '#7DB1FF' },
  { id: 'soiree', label: 'Soirée', icon: '🎉', color: '#8B5CF6' },
  { id: 'bapteme', label: 'Baptême & communion', icon: '🕊️', color: '#5DBCBF' },
  { id: 'garden_party', label: 'Garden party', icon: '🌿', color: '#1F9D7A' },
  { id: 'autre', label: 'Autre', icon: '✨', color: '#CFCFCF' },
];

const getType = (id) => EVENT_TYPES.find(t => t.id === id) || EVENT_TYPES[EVENT_TYPES.length - 1];

const STATUT_COLORS = {
  confirme: { label: 'Confirmé ✓', color: '#1F9D7A', bg: 'rgba(31,157,122,0.1)' },
  refuse: { label: 'Décliné', color: '#D94B5D', bg: 'rgba(217,75,93,0.1)' },
  en_attente: { label: 'En attente', color: '#C58B19', bg: 'rgba(197,139,25,0.1)' },
  invite: { label: 'Invité', color: '#7DB1FF', bg: 'rgba(125,177,255,0.1)' },
};

export default function MonEvenementPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [guestStats, setGuestStats] = useState({ total: 0, confirme: 0, refuse: 0, en_attente: 0, invitation_envoyee: 0 });
  const [reminderSchedule, setReminderSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('infos');
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [newGuest, setNewGuest] = useState({ prenom: '', nom: '', email: '', telephone: '', role: 'invite' });
  const [saving, setSaving] = useState(false);
  const [sendingInvites, setSendingInvites] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [showQR, setShowQR] = useState(null); // guest_id
  const [qrData, setQrData] = useState({}); // guest_id -> { invite_url, qr_png_base64 }
  const [eventQR, setEventQR] = useState(null);
  // Phase 3
  const [linkedDevis, setLinkedDevis] = useState([]);
  // Phase 4 IA
  const [iaLoading, setIaLoading] = useState(null);
  const [iaResults, setIaResults] = useState({});
  const [iaContext, setIaContext] = useState('');
  const [showIAModal, setShowIAModal] = useState(null);
  // Phase 5 Cagnotte
  const [cagnotte, setCagnotte] = useState(null);
  const [showCreateCagnotte, setShowCreateCagnotte] = useState(false);
  const [cagnotteForm, setCagnotteForm] = useState({ titre: '', description: '', objectif: '', type_cagnotte: 'globale', produit_nom: '', produit_prix: '' });
  const [contributeForm, setContributeForm] = useState({ montant: 20, nom_contributeur: '', message: '', anonyme: false });
  const [showContribute, setShowContribute] = useState(false);
  const [creatingCagnotte, setCreatingCagnotte] = useState(false);
  const [contributingLoading, setContributingLoading] = useState(false);
  // Phase 11 Stripe Connect
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  // Phase 6 Photos
  const [photos, setPhotos] = useState([]);
  const [addPhotoUrl, setAddPhotoUrl] = useState('');
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [showPrintOrder, setShowPrintOrder] = useState(false);
  const [printForm, setPrintForm] = useState({ format: '10x15', quantite: 1, finish: 'brillant', livraison_nom: '', livraison_adresse: '', livraison_ville: '', livraison_cp: '', email_contact: '' });
  const [printLoading, setPrintLoading] = useState(false);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  // Phase 8 Partenaires
  const [partners, setPartners] = useState([]);
  // Phase 9 Alertes
  const [smartAlerts, setSmartAlerts] = useState([]);
  const [showCagnotteSuccess, setShowCagnotteSuccess] = useState(false);

  const isOwner = user && event && (user.id === event.owner_user_id || user.role === 'admin');

  useEffect(() => {
    fetchEvent();
  // eslint-disable-next-line
  }, [slug]);

  async function fetchEvent() {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${slug}`);
      if (!r.ok) { setLoading(false); return; }
      const d = await r.json();
      setEvent(d);
      setEditForm({
        title: d.title, event_type: d.event_type,
        date_evenement: d.date_evenement?.slice(0, 10) || '',
        lieu: d.lieu || '', description: d.description || '',
        nombre_invites: d.nombre_invites || '',
        infos_pratiques: d.infos_pratiques || '',
        dress_code: d.dress_code || {},
      });
      // Charger les invités (API V2)
      const rg = await fetch(`${BACKEND}/api/events/${d.event_id}/guests/v2`);
      if (rg.ok) {
        const dg = await rg.json();
        setGuests(dg.guests || []);
        setGuestStats(dg.stats || {});
      }
      // Rappels
      if (d.date_evenement) {
        const rr = await fetch(`${BACKEND}/api/events/${d.event_id}/reminders/schedule`);
        if (rr.ok) { const dr = await rr.json(); setReminderSchedule(dr.schedule || []); }
      }
      // Devis liés
      if (d.devis_links?.length > 0) {
        const devisPromises = d.devis_links.map(dl =>
          fetch(`${BACKEND}/api/devis/${dl.devis_id}`).then(r => r.ok ? r.json() : null).catch(() => null)
        );
        const devisResults = await Promise.all(devisPromises);
        setLinkedDevis(devisResults.filter(Boolean));
      }
      // Résultats IA
      const ri = await fetch(`${BACKEND}/api/events/${d.event_id}/ia/results`);
      if (ri.ok) { const di = await ri.json(); setIaResults(di.results || {}); }
      // Cagnotte
      const rc = await fetch(`${BACKEND}/api/events/${d.event_id}/cagnotte`);
      if (rc.ok) { const dc = await rc.json(); if (dc.exists) setCagnotte(dc); }
      // Photos
      const rph = await fetch(`${BACKEND}/api/events/${d.event_id}/photos`);
      if (rph.ok) { const dph = await rph.json(); setPhotos(dph.photos || []); }
      // Partenaires suggérés
      if (d.event_type) {
        const rp = await fetch(`${BACKEND}/api/partenaires/suggestions/${d.event_type}`);
        if (rp.ok) { const dp = await rp.json(); setPartners(dp.suggestions || []); }
      }
      // Alertes intelligentes
      const ra = await fetch(`${BACKEND}/api/events/${d.event_id}/smart-alerts`);
      if (ra.ok) { const da = await ra.json(); setSmartAlerts(da.alerts || []); }
      // Check cagnotte success URL param
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('cagnotte') === 'success') setShowCagnotteSuccess(true);
      // Stripe Connect status
      if (d.event_id) {
        const rsc = await fetch(`${BACKEND}/api/events/${d.event_id}/stripe-connect/status`);
        if (rsc.ok) { const dsc = await rsc.json(); setConnectStatus(dsc); }
      }
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }

  async function saveEvent() {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const updated = await r.json();
      setEvent(updated);
      setEditMode(false);
      toast.success('Événement mis à jour !');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    setSaving(false);
  }

  async function addGuest() {
    if (!newGuest.prenom && !newGuest.nom && !newGuest.email) {
      toast.error('Entrez au moins un prénom, nom ou email'); return;
    }
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/guests/v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newGuest),
      });
      const g = await r.json();
      setGuests(prev => [...prev, g]);
      setGuestStats(s => ({ ...s, total: s.total + 1, en_attente: (s.en_attente || 0) + 1 }));
      setNewGuest({ prenom: '', nom: '', email: '', telephone: '', role: 'invite' });
      setShowAddGuest(false);
      toast.success('Invité ajouté !');
    } catch { toast.error('Erreur'); }
  }

  async function sendInvitations(guestIds = null, channels = ['email']) {
    setSendingInvites(true);
    try {
      const body = { channels };
      if (guestIds) body.guest_ids = guestIds;
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/invitations/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      toast.success(`${d.sent} invitation(s) envoyée(s) !`);
      // Refresh guests
      const rg = await fetch(`${BACKEND}/api/events/${event.event_id}/guests/v2`);
      if (rg.ok) { const dg = await rg.json(); setGuests(dg.guests || []); setGuestStats(dg.stats || {}); }
    } catch { toast.error('Erreur envoi invitations'); }
    setSendingInvites(false);
  }

  async function sendReminder(type) {
    setSendingReminder(type);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/reminders/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reminder_type: type, channels: ['email'] }),
      });
      const d = await r.json();
      toast.success(`Rappel ${type} envoyé à ${d.sent} invité(s)`);
      // Refresh schedule
      const rr = await fetch(`${BACKEND}/api/events/${event.event_id}/reminders/schedule`);
      if (rr.ok) { const dr = await rr.json(); setReminderSchedule(dr.schedule || []); }
    } catch { toast.error('Erreur envoi rappel'); }
    setSendingReminder(null);
  }

  async function loadGuestQR(guestId) {
    if (qrData[guestId]) { setShowQR(guestId); return; }
    try {
      const r = await fetch(`${BACKEND}/api/guests/${guestId}/qr-url`);
      const d = await r.json();
      setQrData(prev => ({ ...prev, [guestId]: d }));
      setShowQR(guestId);
    } catch { toast.error('Erreur QR'); }
  }

  async function loadEventQR() {
    if (eventQR) return;
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/qr`);
      const d = await r.json();
      setEventQR(d);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
  }

  async function runIA(mode) {
    setIaLoading(mode);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/ia/optimize`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, context: iaContext }),
      });
      const d = await r.json();
      setIaResults(prev => ({ ...prev, [mode]: { result: d.result, generated_at: d.generated_at } }));
      setShowIAModal(mode);
      toast.success(`IA ${IA_MODES[mode]?.label} — Résultat prêt !`);
    } catch { toast.error('Erreur IA'); }
    setIaLoading(null);
  }

  async function createCagnotte() {
    setCreatingCagnotte(true);
    try {
      const body = {
        titre: cagnotteForm.titre,
        description: cagnotteForm.description || undefined,
        objectif: cagnotteForm.objectif ? parseFloat(cagnotteForm.objectif) : undefined,
        type_cagnotte: cagnotteForm.type_cagnotte,
        produit_nom: cagnotteForm.produit_nom || undefined,
        produit_prix: cagnotteForm.produit_prix ? parseFloat(cagnotteForm.produit_prix) : undefined,
      };
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/cagnotte`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      setCagnotte(d);
      setShowCreateCagnotte(false);
      toast.success('Cagnotte créée !');
    } catch { toast.error('Erreur création cagnotte'); }
    setCreatingCagnotte(false);
  }

  async function contribute() {
    if (!contributeForm.montant || contributeForm.montant < 1) { toast.error('Montant minimum : 1€'); return; }
    setContributingLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/cagnotte/contribute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contributeForm),
      });
      const d = await r.json();
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
      } else if (d.simulated) {
        toast.success('Contribution enregistrée (simulation) !');
        setShowContribute(false);
        const rc = await fetch(`${BACKEND}/api/events/${event.event_id}/cagnotte`);
        if (rc.ok) { const dc = await rc.json(); if (dc.exists) setCagnotte(dc); }
      }
    } catch { toast.error('Erreur paiement'); }
    setContributingLoading(false);
  }

  async function addPhoto() {
    if (!addPhotoUrl.trim()) return;
    setAddingPhoto(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: addPhotoUrl, source: 'upload' }),
      });
      const ph = await r.json();
      setPhotos(prev => [ph, ...prev]);
      setAddPhotoUrl('');
      toast.success('Photo ajoutée !');
    } catch { toast.error('Erreur'); }
    setAddingPhoto(false);
  }

  async function deletePhoto(photo_id) {
    await fetch(`${BACKEND}/api/events/${event.event_id}/photos/${photo_id}`, { method: 'DELETE' });
    setPhotos(prev => prev.filter(p => p.photo_id !== photo_id));
    toast.success('Photo supprimée');
  }

  async function activateStripeConnect() {
    setConnectLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/stripe-connect/onboard`, { method: 'POST' });
      const d = await r.json();
      if (d.onboarding_url) {
        window.open(d.onboarding_url, '_blank');
      } else if (d.simulated || d.already_active) {
        setConnectStatus({ has_connect: true, charges_enabled: true, status: d.simulated ? 'simulated' : 'actif' });
        toast.success(d.message || 'Compte de paiement activé !');
      }
    } catch { toast.error('Erreur activation paiements'); }
    setConnectLoading(false);
  }

  async function submitPrintOrder() {    if (selectedPhotos.length === 0) { toast.error('Sélectionnez au moins une photo'); return; }
    if (!printForm.livraison_nom || !printForm.livraison_adresse) { toast.error('Renseignez l\'adresse de livraison'); return; }
    setPrintLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/events/${event.event_id}/photos/print-order`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...printForm, photos: selectedPhotos, quantite: parseInt(printForm.quantite) }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(d.message);
        setShowPrintOrder(false);
        setSelectedPhotos([]);
      }
    } catch { toast.error('Erreur commande'); }
    setPrintLoading(false);
  }

  async function removeGuest(guest_id) {
    await fetch(`${BACKEND}/api/events/${event.event_id}/guests/${guest_id}/v2`, { method: 'DELETE' });
    setGuests(prev => prev.filter(g => g.guest_id !== guest_id));
    setGuestStats(s => ({ ...s, total: Math.max(0, s.total - 1) }));
    toast.success('Invité supprimé');
  }

  const copyShareLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/mon-evenement/${slug}`);
    toast.success('Lien de partage copié !');
  };

  if (loading) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}><Header />
      <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
        <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid var(--border)', borderTopColor: 'var(--teal)' }} />
      </div>
    </div>
  );

  if (!event) return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}><Header />
      <div className="container-ose text-center" style={{ paddingTop: '6rem' }}>
        <PartyPopper size={48} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
        <h1 className="font-display text-2xl font-bold mb-3" style={{ color: 'var(--ink)' }}>Événement non trouvé</h1>
        <Link to="/mes-evenements" className="btn-teal px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center gap-2">
          Mes événements
        </Link>
      </div><Footer />
    </div>
  );

  const type = getType(event.event_type);
  const dateLabel = event.date_evenement
    ? formatDateWithDay(event.date_evenement)
    : 'Date à définir';
  const daysLeft = event.date_evenement
    ? Math.ceil((new Date(event.date_evenement) - Date.now()) / 86400000)
    : null;

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO ÉVÉNEMENT */}
      <div className="relative overflow-hidden" style={{ minHeight: 220 }}>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${type.color}18 0%, ${type.color}08 50%, transparent 100%)`,
        }} />
        <div className="container-ose relative z-10" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Link to="/mes-evenements" className="text-xs" style={{ color: 'var(--text-muted)' }}>← Mes événements</Link>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">{type.icon}</span>
                <div>
                  <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                    {event.title}
                  </h1>
                  <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold mt-1"
                    style={{ background: `${type.color}15`, color: type.color }}>
                    {event.event_type_label || type.label}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                  <CalendarDays size={14} style={{ color: type.color }} /> {dateLabel}
                </span>
                {event.lieu && (
                  <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <MapPin size={14} style={{ color: type.color }} /> {event.lieu}
                  </span>
                )}
                {event.nombre_invites && (
                  <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Users size={14} style={{ color: type.color }} /> {event.nombre_invites} personnes attendues
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {daysLeft !== null && daysLeft >= 0 && (
                <span className="px-3 py-1.5 rounded-xl text-sm font-bold"
                  style={{
                    background: daysLeft <= 7 ? 'rgba(244,167,178,0.15)' : 'var(--teal-mist)',
                    color: daysLeft <= 7 ? '#D94B5D' : '#0B4B4E',
                  }}
                >
                  ⏳ J-{daysLeft}
                </span>
              )}
              <button onClick={copyShareLink}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
                style={{ background: 'rgba(255,255,255,0.8)', color: 'var(--ink)', border: '1px solid var(--border)', backdropFilter: 'blur(8px)' }}
              >
                <Share2 size={15} /> Partager
              </button>
              {isOwner && (
                <button onClick={() => setEditMode(!editMode)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold"
                  style={{ background: type.color, color: '#0B1E20' }}
                >
                  <Edit3 size={15} /> {editMode ? 'Annuler' : 'Modifier'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container-ose" style={{ paddingBottom: '5rem' }}>

        {/* MODE ÉDITION */}
        {editMode && isOwner && (
          <div className="mb-6 p-5 rounded-2xl" style={{ background: '#FFFFFF', border: `1px solid ${type.color}30` }}>
            <h2 className="font-semibold text-sm mb-4" style={{ color: type.color }}>Modifier l'événement</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              {[
                { key: 'title', label: 'Nom', type: 'text' },
                { key: 'lieu', label: 'Lieu', type: 'text' },
                { key: 'date_evenement', label: 'Date', type: 'date' },
                { key: 'nombre_invites', label: 'Nb invités', type: 'number' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                  <input type={f.type} value={editForm[f.key] || ''}
                    onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl text-sm"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
                  />
                </div>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Description</label>
              <textarea rows={3} value={editForm.description || ''}
                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm resize-none"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
              />
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Informations pratiques (parking, accès, hébergement…)</label>
              <textarea rows={2} value={editForm.infos_pratiques || ''}
                onChange={e => setEditForm(p => ({ ...p, infos_pratiques: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm resize-none"
                style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
              />
            </div>
            <button onClick={saveEvent} disabled={saving}
              className="btn-teal px-6 py-2.5 rounded-xl text-sm font-bold disabled:opacity-40"
            >
              {saving ? 'Sauvegarde…' : '✓ Enregistrer'}
            </button>
          </div>
        )}

        {/* TABS */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { id: 'infos', label: 'Tableau de bord', icon: <Info size={14} /> },
            { id: 'guests', label: `Invités (${guestStats.total})`, icon: <Users size={14} /> },
            { id: 'rappels', label: 'Rappels', icon: <Bell size={14} /> },
            { id: 'ia', label: '✨ IA', icon: <Sparkles size={14} /> },
            { id: 'cagnotte', label: '💰 Cagnotte', icon: <CreditCard size={14} /> },
            { id: 'photos', label: '📸 Photos', icon: <Image size={14} /> },
            { id: 'partenaires', label: '🤝 Partenaires', icon: <Heart size={14} /> },
            { id: 'devis', label: 'Devis', icon: <FileText size={14} /> },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`event-tab-${t.id}`}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all"
              style={{
                background: activeTab === t.id ? type.color : '#FFFFFF',
                color: activeTab === t.id ? '#0B1E20' : 'var(--text-muted)',
                border: `1px solid ${activeTab === t.id ? type.color : 'var(--border)'}`,
                fontSize: 12,
              }}
            >{t.icon} {t.label}</button>
          ))}
        </div>

        {/* TAB : INFOS */}
        {activeTab === 'infos' && (
          <div className="space-y-4">

            {/* ── Alertes intelligentes ── */}
            {smartAlerts.filter(a => ['urgent', 'warning'].includes(a.type)).length > 0 && (
              <div className="space-y-2">
                {smartAlerts.filter(a => ['urgent', 'warning'].includes(a.type)).map((alert, i) => (
                  <button key={`alert-${alert.type}-${i}`} onClick={() => setActiveTab(alert.action || 'infos')}
                    className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                    style={{
                      background: alert.type === 'urgent' ? 'rgba(217,75,93,0.08)' : 'rgba(197,139,25,0.08)',
                      border: `1px solid ${alert.type === 'urgent' ? 'rgba(217,75,93,0.25)' : 'rgba(197,139,25,0.25)'}`,
                    }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{alert.icon}</span>
                    <p className="text-xs font-semibold flex-1" style={{ color: alert.type === 'urgent' ? '#D94B5D' : '#C58B19' }}>
                      {alert.message}
                    </p>
                    <ArrowRight size={13} style={{ color: alert.type === 'urgent' ? '#D94B5D' : '#C58B19', flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            )}

            {/* Suggestions IA */}
            {smartAlerts.filter(a => a.type === 'suggestion').map((alert, i) => (
              <button key={`alert-${alert.type}-${i}`} onClick={() => setActiveTab(alert.action || 'cagnotte')}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left"
                style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)' }}>
                <span style={{ fontSize: 18 }}>{alert.icon}</span>
                <p className="text-xs font-medium flex-1" style={{ color: '#8B5CF6' }}>{alert.message}</p>
                <ArrowRight size={13} style={{ color: '#8B5CF6', flexShrink: 0 }} />
              </button>
            ))}

            {/* ── Statut global + Prochaines actions ── */}
            {(() => {
              const hasDevis = (event.devis_links?.length > 0);
              const hasInvites = guestStats.total > 0;
              const hasDate = !!event.date_evenement;
              const invitesSent = (guestStats.invitation_envoyee || 0) > 0;
              let statutLabel = '🔧 En préparation';
              let statutColor = '#C58B19';
              let statutBg = 'rgba(197,139,25,0.08)';
              let statutNote = 'Complétez les étapes ci-dessous';
              if (hasDate && hasDevis && hasInvites && invitesSent) {
                statutLabel = '✅ Prêt'; statutColor = '#1F9D7A'; statutBg = 'rgba(31,157,122,0.08)'; statutNote = 'Tout est en ordre !';
              } else if (hasDate && hasDevis) {
                statutLabel = '⏳ En attente'; statutColor = '#7DB1FF'; statutBg = 'rgba(125,177,255,0.08)'; statutNote = 'Invitations à envoyer';
              }
              const actions = [];
              if (!hasDate) actions.push({ label: 'Définir la date de l\'événement', color: '#D94B5D', action: () => setEditMode(true) });
              if (!hasInvites) actions.push({ label: 'Ajouter vos premiers invités', color: '#C58B19', action: () => setActiveTab('guests') });
              if (hasInvites && !invitesSent) actions.push({ label: 'Envoyer les invitations', color: '#C58B19', action: () => setActiveTab('guests') });
              if (!hasDevis) actions.push({ label: 'Créer votre devis', color: '#5DBCBF', link: '/creer-evenement' });
              if (linkedDevis.some(d => d.statut === 'signe')) actions.push({ label: '⚠️ Payer l\'acompte pour bloquer la date', color: '#D94B5D', link: `/devis/${linkedDevis[0]?.id}` });
              return (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-2xl" style={{ background: statutBg, border: `1px solid ${statutColor}25` }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Statut</p>
                    <p className="text-lg font-bold" style={{ color: statutColor }}>{statutLabel}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{statutNote}</p>
                  </div>
                  {actions.length > 0 && (
                    <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--ink)' }}>⚡ Prochaines actions</p>
                      <div className="space-y-2">
                        {actions.slice(0, 4).map((a, i) => (
                          a.link ? (
                            <Link key={`mev-item-${i}`} to={a.link} className="flex items-center gap-2 text-xs font-medium" style={{ color: a.color }}>
                              <ArrowRight size={12} />{a.label}
                            </Link>
                          ) : (
                            <button key={`mev-item-${i}`} onClick={a.action} className="flex items-center gap-2 text-xs font-medium text-left"
                              style={{ color: a.color, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                              <ArrowRight size={12} />{a.label}
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {event.description && (
              <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: type.color }}>Description</h3>
                <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: 1.75 }}>{event.description}</p>
              </div>
            )}

            {event.infos_pratiques && (
              <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: type.color }}>Informations pratiques</h3>
                <p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: 1.75 }}>{event.infos_pratiques}</p>
              </div>
            )}

            {event.dress_code && Object.keys(event.dress_code).length > 0 && (
              <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <h3 className="font-semibold text-sm mb-2" style={{ color: type.color }}>Dress code</h3>
                {event.dress_code.style && (
                  <p className="text-sm font-medium mb-1" style={{ color: 'var(--ink)' }}>{event.dress_code.style}</p>
                )}
                {event.dress_code.couleurs && (
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Couleurs : {event.dress_code.couleurs}</p>
                )}
              </div>
            )}

            {/* Stats avancées — Taux + progression */}
            {guestStats.total > 0 && (() => {
              const responded = (guestStats.confirme || 0) + (guestStats.refuse || 0);
              const tauxReponse = Math.round(responded / guestStats.total * 100);
              const nbConfirmes = guestStats.confirme || 0;
              const progressPct = Math.round(nbConfirmes / guestStats.total * 100);
              return (
                <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <h3 className="font-semibold text-sm mb-4" style={{ color: type.color }}>📊 Tableau de bord invités</h3>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { icon: <UserCheck size={18} />, value: guestStats.confirme || 0, label: 'Confirmés', color: '#1F9D7A' },
                      { icon: <HelpCircle size={18} />, value: guestStats.en_attente || 0, label: 'En attente', color: '#C58B19' },
                      { icon: <UserX size={18} />, value: guestStats.refuse || 0, label: 'Déclinés', color: '#D94B5D' },
                    ].map((k, i) => (
                      <div key={`list-item-${i}`} className="p-3 rounded-xl text-center"
                        style={{ background: `${k.color}08`, border: `1px solid ${k.color}20` }}>
                        <div style={{ color: k.color, display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{k.icon}</div>
                        <p className="text-xl font-bold" style={{ color: k.color }}>{k.value}</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{k.label}</p>
                      </div>
                    ))}
                  </div>
                  {/* Taux de réponse */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>Taux de réponse</p>
                      <p className="text-xs font-bold" style={{ color: type.color }}>{tauxReponse}%</p>
                    </div>
                    <div className="w-full rounded-full overflow-hidden" style={{ height: 8, background: 'var(--bg)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${tauxReponse}%`, background: `linear-gradient(90deg, ${type.color} 0%, ${type.color}AA 100%)` }} />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{responded}/{guestStats.total} invités ont répondu</p>
                  </div>
                  {/* Progression confirmés */}
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <p className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>Présences confirmées</p>
                      <p className="text-xs font-bold" style={{ color: '#1F9D7A' }}>{progressPct}%</p>
                    </div>
                    <div className="w-full rounded-full overflow-hidden" style={{ height: 8, background: 'var(--bg)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, background: 'linear-gradient(90deg, #1F9D7A, #0B4B4E)' }} />
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Lien configurateur */}
            <div className="p-4 rounded-2xl flex items-center justify-between gap-4"
              style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <div>
                <p className="font-semibold text-sm" style={{ color: '#0B4B4E' }}>Ajoutez des animations pour cet événement</p>
                <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>Photobooth, barbe à papa, jeux gonflables…</p>
              </div>
              <Link to="/creer-evenement"
                className="btn-teal px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex items-center gap-1.5">
                Configurateur <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        )}

        {/* TAB : INVITÉS */}
        {activeTab === 'guests' && (
          <div className="space-y-4">

            {/* Stats bar */}
            {guests.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Total', value: guestStats.total, color: 'var(--ink)', bg: '#FFFFFF' },
                  { label: 'Confirmés ✓', value: guestStats.confirme || 0, color: '#1F9D7A', bg: 'rgba(31,157,122,0.08)' },
                  { label: 'En attente', value: guestStats.en_attente || 0, color: '#C58B19', bg: 'rgba(197,139,25,0.08)' },
                  { label: 'Refusés', value: guestStats.refuse || 0, color: '#D94B5D', bg: 'rgba(217,75,93,0.08)' },
                ].map((k, i) => (
                  <div key={`list-item-${i}`} className="p-3 rounded-xl text-center"
                    style={{ background: k.bg, border: `1px solid ${k.color}20` }}>
                    <p className="text-xl font-bold font-mono-ose" style={{ color: k.color }}>{k.value}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{k.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Boutons action globaux */}
            {isOwner && guests.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="send-all-invitations-btn"
                  onClick={() => sendInvitations(null, ['email'])}
                  disabled={sendingInvites}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                  style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.25)' }}
                >
                  {sendingInvites ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                  Envoyer toutes les invitations
                </button>
                <button
                  onClick={() => { loadEventQR(); }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                  style={{ background: 'rgba(212,175,55,0.08)', color: '#C58B19', border: '1px solid rgba(212,175,55,0.2)' }}
                >
                  <QrCode size={14} /> QR événement
                </button>
              </div>
            )}

            {/* QR événement */}
            {eventQR && (
              <div className="p-4 rounded-2xl text-center" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>QR Code — Page événement</p>
                {eventQR.qr_png_base64 && (
                  <img src={`data:image/png;base64,${eventQR.qr_png_base64}`}
                    alt="QR événement" className="mx-auto" style={{ width: 160, height: 160, imageRendering: 'pixelated' }} loading="lazy" />
                )}
                <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                  <a href={eventQR.event_url} target="_blank" rel="noreferrer" style={{ color: 'var(--teal)' }}>
                    {eventQR.event_url}
                  </a>
                </p>
              </div>
            )}

            {/* Barre ajout */}
            {isOwner && (
              <div>
                {!showAddGuest ? (
                  <button
                    data-testid="add-guest-btn"
                    onClick={() => setShowAddGuest(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: 'var(--teal-mist)', color: '#0B4B4E', border: '1px solid rgba(93,188,191,0.2)' }}
                  >
                    <Plus size={15} /> Ajouter un invité
                  </button>
                ) : (
                  <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <p className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>Nouvel invité</p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      {[
                        { key: 'prenom', label: 'Prénom', placeholder: 'Jean' },
                        { key: 'nom', label: 'Nom', placeholder: 'Dupont' },
                        { key: 'email', label: 'Email', placeholder: 'jean@email.fr', type: 'email' },
                        { key: 'telephone', label: 'Téléphone', placeholder: '06 12 34 56 78', type: 'tel' },
                      ].map(f => (
                        <div key={f.key}>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                          <input type={f.type || 'text'} placeholder={f.placeholder}
                            value={newGuest[f.key]}
                            onChange={e => setNewGuest(p => ({ ...p, [f.key]: e.target.value }))}
                            className="w-full px-3 py-2 rounded-xl text-sm"
                            style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
                          />
                        </div>
                      ))}
                    </div>
                    {/* Rôle */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Rôle</label>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          { id: 'invite', label: 'Invité' },
                          { id: 'temoin', label: '⭐ Témoin' },
                          { id: 'organisateur', label: '🎯 Organisateur' },
                        ].map(r => (
                          <button key={r.id} onClick={() => setNewGuest(p => ({ ...p, role: r.id }))}
                            className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                            style={{
                              background: newGuest.role === r.id ? type.color : '#FFFFFF',
                              color: newGuest.role === r.id ? '#0B1E20' : 'var(--text-muted)',
                              border: `1px solid ${newGuest.role === r.id ? type.color : 'var(--border)'}`,
                            }}>
                            {r.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="add-guest-submit"
                        onClick={addGuest} className="btn-teal px-4 py-2 rounded-xl text-sm font-semibold">
                        Ajouter
                      </button>
                      <button onClick={() => setShowAddGuest(false)}
                        className="px-4 py-2 rounded-xl text-sm" style={{ color: 'var(--text-muted)' }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Liste invités */}
            {guests.length === 0 ? (
              <div className="text-center py-16">
                <Users size={40} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun invité pour l'instant</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                {guests.map((g, i) => {
                  const sc = STATUT_COLORS[g.statut] || STATUT_COLORS.invite;
                  const gName = [g.prenom, g.nom].filter(Boolean).join(' ') || g.email || 'Invité';
                  const initiale = gName[0]?.toUpperCase() || '?';
                  return (
                    <div key={g.guest_id}
                      className="flex items-center gap-3 px-4 py-3"
                      style={{ borderBottom: i < guests.length - 1 ? '1px solid var(--border)' : 'none' }}
                    >
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                        style={{ background: `${type.color}15`, color: type.color }}
                      >
                        {initiale}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{gName}</p>
                          {g.role && g.role !== 'invite' && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full"
                              style={{ background: `${type.color}15`, color: type.color, fontSize: 10 }}>
                              {g.role === 'temoin' ? '⭐ Témoin' : '🎯 Organisateur'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          {[g.email, g.telephone].filter(Boolean).join(' · ')}
                        </p>
                        {g.invitation_sent_at && (
                          <p className="text-xs" style={{ color: 'rgba(93,188,191,0.8)' }}>
                            ✉️ Invitation envoyée
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-xs font-medium px-2 py-1 rounded-full"
                          style={{ background: sc.bg, color: sc.color }}>
                          {sc.label}
                        </span>
                        {isOwner && (
                          <>
                            {(g.email || g.telephone) && (
                              <button
                                onClick={() => sendInvitations([g.guest_id], g.email ? ['email'] : ['sms'])}
                                className="p-1.5 rounded-lg transition-colors"
                                style={{ background: 'var(--teal-mist)', color: 'var(--teal)' }}
                                title="Envoyer invitation"
                              >
                                <Send size={12} />
                              </button>
                            )}
                            <button
                              onClick={() => loadGuestQR(g.guest_id)}
                              className="p-1.5 rounded-lg transition-colors"
                              style={{ background: 'rgba(212,175,55,0.1)', color: '#C58B19' }}
                              title="QR code"
                            >
                              <QrCode size={12} />
                            </button>
                            <button onClick={() => removeGuest(g.guest_id)}
                              className="p-1.5 rounded-lg" style={{ color: '#D94B5D', background: 'rgba(217,75,93,0.06)' }}>
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Modal QR invité */}
            {showQR && qrData[showQR] && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{ background: 'rgba(26,26,46,0.5)', backdropFilter: 'blur(8px)' }}>
                <div className="rounded-2xl p-6 text-center" style={{ background: '#FFFFFF', maxWidth: 300, width: '100%' }}>
                  <p className="font-semibold text-sm mb-4" style={{ color: 'var(--ink)' }}>QR Code d'invitation</p>
                  {qrData[showQR].qr_png_base64 && (
                    <img src={`data:image/png;base64,${qrData[showQR].qr_png_base64}`}
                      alt="QR invitation" className="mx-auto mb-3"
                      style={{ width: 180, height: 180, imageRendering: 'pixelated' }} loading="lazy" />
                  )}
                  <p className="text-xs mb-4" style={{ color: 'var(--text-muted)', wordBreak: 'break-all' }}>
                    {qrData[showQR].invite_url}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { navigator.clipboard?.writeText(qrData[showQR].invite_url); toast.success('Lien copié !'); }}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold"
                      style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                      Copier le lien
                    </button>
                    <button onClick={() => setShowQR(null)}
                      className="flex-1 py-2 rounded-xl text-xs font-semibold"
                      style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB : RAPPELS */}
        {activeTab === 'rappels' && (
          <div className="space-y-4">
            {!event.date_evenement ? (
              <div className="p-5 rounded-2xl text-center" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <Bell size={36} className="mx-auto mb-3" style={{ color: 'var(--border)' }} />
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--ink)' }}>Date de l'événement requise</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Définissez une date pour activer les rappels automatiques.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 rounded-2xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#0B4B4E' }}>Rappels automatiques</p>
                  <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>
                    Envoyez des rappels aux invités qui n'ont pas encore répondu.
                    Les invités ayant refusé sont exclus automatiquement.
                  </p>
                </div>
                {reminderSchedule.map(r => {
                  // Variables nommées pour éviter les ternaires imbriqués
                  const reminderBg = r.sent ? 'rgba(31,157,122,0.08)' : r.due ? 'rgba(197,139,25,0.1)' : 'var(--teal-mist)';
                  const reminderColor = r.sent ? '#1F9D7A' : r.due ? '#C58B19' : '#0B4B4E';
                  const reminderBorder = r.sent ? 'rgba(31,157,122,0.2)' : r.due ? 'rgba(197,139,25,0.2)' : 'rgba(93,188,191,0.2)';
                  const cardBorder = r.sent ? 'rgba(31,157,122,0.2)' : r.due ? 'rgba(197,139,25,0.2)' : 'var(--border)';
                  return (
                  <div key={r.type} className="flex items-center justify-between p-4 rounded-2xl"
                    style={{ background: '#FFFFFF', border: `1px solid ${cardBorder}` }}>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>
                        {r.type.toUpperCase()} — {r.label}
                      </p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Prévu le {formatDate(r.target_date)}
                      </p>
                      {r.sent && <p className="text-xs" style={{ color: '#1F9D7A' }}>✓ Envoyé</p>}
                      {!r.sent && r.due && <p className="text-xs" style={{ color: '#C58B19' }}>⚠️ Dû — envoyez maintenant</p>}
                    </div>
                    {isOwner && (
                      <button
                        onClick={() => sendReminder(r.type)}
                        disabled={sendingReminder === r.type}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50"
                        style={{ background: reminderBg, color: reminderColor, border: `1px solid ${reminderBorder}` }}
                      >
                        {sendingReminder === r.type
                          ? <RefreshCw size={12} className="animate-spin" />
                          : <Send size={12} />}
                        {r.sent ? 'Renvoyer' : 'Envoyer'}
                      </button>
                    )}
                  </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* TAB : CAGNOTTE */}
        {activeTab === 'cagnotte' && (
          <div className="space-y-4">

            {/* ── Stripe Connect — Activer paiements ── */}
            {isOwner && (() => {
              // Variables nommées pour éviter les ternaires imbriqués
              const isActive = connectStatus?.charges_enabled;
              const isPending = connectStatus?.status === 'pending';
              const connectBg = isActive ? 'rgba(31,157,122,0.08)' : isPending ? 'rgba(197,139,25,0.08)' : 'rgba(139,92,246,0.08)';
              const connectBorder = isActive ? 'rgba(31,157,122,0.25)' : isPending ? 'rgba(197,139,25,0.25)' : 'rgba(139,92,246,0.2)';
              const dotColor = isActive ? '#1F9D7A' : isPending ? '#C58B19' : '#8B5CF6';
              const statusLabel = isActive ? '✅ Paiements activés' : isPending ? '⏳ Vérification en cours' : '💳 Activer mes paiements';
              const statusMsg = isActive
                ? 'Vos paiements sont sécurisés et envoyés directement sur votre compte.'
                : isPending
                ? 'Complétez votre vérification Stripe pour activer les virements.'
                : 'Activez votre compte pour recevoir les contributions de la cagnotte directement sur votre IBAN.';
              const btnLabel = isPending ? 'Continuer' : 'Activer';
              const btnBg = isPending ? '#C58B19' : '#8B5CF6';
              return (
              <div className="p-4 rounded-2xl" style={{ background: connectBg, border: `1px solid ${connectBorder}` }}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}` }} />
                      <p className="font-bold text-sm" style={{ color: dotColor }}>
                        {statusLabel}
                      </p>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {statusMsg}
                    </p>
                  </div>
                  {!isActive && (
                    <button
                      data-testid="activate-stripe-connect-btn"
                      onClick={activateStripeConnect}
                      disabled={connectLoading}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap disabled:opacity-50 flex-shrink-0"
                      style={{ background: btnBg, color: '#FFFFFF' }}>
                      {connectLoading ? <RefreshCw size={13} className="animate-spin" /> : <CreditCard size={13} />}
                      {btnLabel}
                    </button>
                  )}
                </div>
                {isActive && (
                  <p className="text-xs" style={{ color: '#1F9D7A', fontWeight: 600 }}>
                    🔒 Vos virements sont traités directement via Stripe — sécurisé et automatisé.
                  </p>
                )}
              </div>
              );
            })()}

            {/* Bouton participer public */}
            {cagnotte && (
              <div className="p-5 rounded-2xl text-center"
                style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(93,188,191,0.08))', border: '1px solid rgba(212,175,55,0.3)' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>💰</div>
                <h3 className="font-bold text-base mb-1" style={{ color: 'var(--ink)' }}>{cagnotte.titre}</h3>
                {cagnotte.description && <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>{cagnotte.description}</p>}

                {/* Progression */}
                {cagnotte.objectif && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span style={{ color: 'var(--text-muted)' }}>Collecté</span>
                      <span className="font-bold" style={{ color: '#D4AF37' }}>
                        {(cagnotte.total_collecte || 0).toFixed(0)}€ / {cagnotte.objectif}€
                      </span>
                    </div>
                    <div className="w-full rounded-full overflow-hidden" style={{ height: 10, background: 'var(--bg)' }}>
                      <div className="h-full rounded-full"
                        style={{ width: `${cagnotte.pct_objectif || 0}%`, background: 'linear-gradient(90deg, #D4AF37, #C58B19)' }} />
                    </div>
                    <p className="text-xs mt-1 text-right" style={{ color: '#D4AF37', fontWeight: 600 }}>
                      {cagnotte.pct_objectif || 0}% atteint
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold price-gold">{(cagnotte.total_collecte || 0).toFixed(0)}€</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>collectés</p>
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <p className="text-xl font-bold" style={{ color: '#5DBCBF' }}>{cagnotte.nb_contributeurs || 0}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>participants</p>
                  </div>
                </div>

                <button
                  data-testid="participate-cagnotte-btn"
                  onClick={() => setShowContribute(!showContribute)}
                  className="w-full btn-teal py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                >
                  <CreditCard size={16} /> Participer à l'événement
                </button>

                {/* Info commission transparente */}
                <p className="text-xs text-center mt-2" style={{ color: 'var(--text-muted)' }}>
                  5% de frais de plateforme · Le reste va directement à l'organisateur
                </p>


                {/* Formulaire contribution */}
                {showContribute && (
                  <div className="mt-4 p-4 rounded-xl text-left" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <p className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>Votre participation</p>
                    <div className="mb-3">
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Montant (€)</label>
                      <div className="flex gap-2 flex-wrap mb-2">
                        {[10, 20, 50, 100].map(m => (
                          <button key={m} onClick={() => setContributeForm(p => ({ ...p, montant: m }))}
                            className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                            style={{
                              background: contributeForm.montant === m ? 'var(--teal)' : '#FEFAF5',
                              color: contributeForm.montant === m ? '#0B1E20' : 'var(--text-muted)',
                              border: `1px solid ${contributeForm.montant === m ? 'var(--teal)' : 'var(--border)'}`,
                            }}>{m}€</button>
                        ))}
                        <input type="number" min="1" placeholder="Autre"
                          value={contributeForm.montant}
                          onChange={e => setContributeForm(p => ({ ...p, montant: parseInt(e.target.value) || 0 }))}
                          className="w-20 px-2 py-1.5 rounded-xl text-xs"
                          style={{ background: '#FEFAF5', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Votre nom (facultatif)</label>
                        <input type="text" placeholder="Prénom Nom"
                          value={contributeForm.nom_contributeur}
                          onChange={e => setContributeForm(p => ({ ...p, nom_contributeur: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-xs"
                          style={{ background: '#FEFAF5', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Message (facultatif)</label>
                        <input type="text" placeholder="Joyeux anniversaire !"
                          value={contributeForm.message}
                          onChange={e => setContributeForm(p => ({ ...p, message: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-xs"
                          style={{ background: '#FEFAF5', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-xs mb-3" style={{ color: 'var(--text-muted)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={contributeForm.anonyme}
                        onChange={e => setContributeForm(p => ({ ...p, anonyme: e.target.checked }))} />
                      Rester anonyme
                    </label>
                    <button onClick={contribute} disabled={contributingLoading || !contributeForm.montant}
                      className="w-full btn-teal py-3 rounded-xl text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2">
                      {contributingLoading ? <RefreshCw size={15} className="animate-spin" /> : <CreditCard size={15} />}
                      Payer {contributeForm.montant}€ — Stripe sécurisé
                    </button>
                  </div>
                )}

                {/* Liste contributeurs avec commission */}
                {cagnotte.contributions?.length > 0 && (
                  <div className="mt-4 text-left">
                    <p className="text-xs font-semibold mb-2" style={{ color: 'var(--ink)' }}>Contributeurs</p>
                    <div className="space-y-1.5">
                      {cagnotte.contributions.slice(0, 5).map((c, i) => (
                        <div key={`mev-item-${i}`} className="flex items-center justify-between text-xs">
                          <span style={{ color: 'var(--text-muted)' }}>{c.anonyme ? 'Anonyme' : c.nom_contributeur || 'Participant'}</span>
                          <div className="text-right">
                            <span className="price-gold font-bold">{c.montant}€</span>
                            {c.montant_net_organisateur && (
                              <span className="text-xs block" style={{ color: '#1F9D7A' }}>
                                → {c.montant_net_organisateur}€ net
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Créer une cagnotte */}
            {isOwner && !cagnotte && (
              <div>
                {!showCreateCagnotte ? (
                  <div className="text-center py-12">
                    <div style={{ fontSize: 48, marginBottom: 12 }}>💰</div>
                    <p className="font-semibold text-base mb-2" style={{ color: 'var(--ink)' }}>Créer une cagnotte</p>
                    <p className="text-xs mb-6" style={{ color: 'var(--text-muted)', maxWidth: 300, margin: '0 auto 20px' }}>
                      Permettez à vos invités de participer financièrement à votre événement.
                      Les paiements sont traités par Stripe et trackés dans votre tableau de bord.
                    </p>
                    <button
                      data-testid="create-cagnotte-btn"
                      onClick={() => setShowCreateCagnotte(true)}
                      className="btn-teal px-6 py-3 rounded-xl text-sm font-bold inline-flex items-center gap-2">
                      <CreditCard size={15} /> Créer la cagnotte
                    </button>
                  </div>
                ) : (
                  <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                    <p className="font-semibold text-sm mb-4" style={{ color: 'var(--ink)' }}>Paramètres de la cagnotte</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Titre *</label>
                        <input type="text" placeholder="Ex: Cagnotte anniversaire Lucas"
                          value={cagnotteForm.titre}
                          onChange={e => setCagnotteForm(p => ({ ...p, titre: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl text-sm"
                          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Description</label>
                        <input type="text" placeholder="Ex: Pour financer le photobooth et les animations"
                          value={cagnotteForm.description}
                          onChange={e => setCagnotteForm(p => ({ ...p, description: e.target.value }))}
                          className="w-full px-3 py-2.5 rounded-xl text-sm"
                          style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Objectif (€)</label>
                          <input type="number" placeholder="500" min="0"
                            value={cagnotteForm.objectif}
                            onChange={e => setCagnotteForm(p => ({ ...p, objectif: e.target.value }))}
                            className="w-full px-3 py-2.5 rounded-xl text-sm"
                            style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Type</label>
                          <select value={cagnotteForm.type_cagnotte}
                            onChange={e => setCagnotteForm(p => ({ ...p, type_cagnotte: e.target.value }))}
                            className="w-full px-3 py-2.5 rounded-xl text-sm"
                            style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                            <option value="globale">Cagnotte globale</option>
                            <option value="produit">Financer un produit</option>
                          </select>
                        </div>
                      </div>
                      {cagnotteForm.type_cagnotte === 'produit' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Produit ciblé</label>
                            <input type="text" placeholder="Photobooth 360°"
                              value={cagnotteForm.produit_nom}
                              onChange={e => setCagnotteForm(p => ({ ...p, produit_nom: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl text-xs"
                              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Prix produit (€)</label>
                            <input type="number" placeholder="350"
                              value={cagnotteForm.produit_prix}
                              onChange={e => setCagnotteForm(p => ({ ...p, produit_prix: e.target.value }))}
                              className="w-full px-3 py-2 rounded-xl text-xs"
                              style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2 pt-2">
                        <button onClick={createCagnotte} disabled={creatingCagnotte || !cagnotteForm.titre}
                          className="flex-1 btn-teal py-3 rounded-xl text-sm font-bold disabled:opacity-40">
                          {creatingCagnotte ? 'Création…' : 'Créer la cagnotte'}
                        </button>
                        <button onClick={() => setShowCreateCagnotte(false)}
                          className="px-4 py-3 rounded-xl text-sm" style={{ color: 'var(--text-muted)' }}>
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB : PHOTOS */}
        {activeTab === 'photos' && (
          <div className="space-y-4">
            {/* Success cagnotte */}
            {showCagnotteSuccess && (
              <div className="p-5 rounded-2xl text-center" style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.25)' }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
                <p className="font-bold text-base mb-2" style={{ color: '#1F9D7A' }}>Merci pour votre participation !</p>
                <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>Votre contribution a bien été enregistrée.</p>
                <button onClick={() => setActiveTab('infos')} className="btn-teal px-5 py-2.5 rounded-xl text-sm font-bold inline-flex items-center gap-2">
                  <ArrowRight size={14} /> Voir l'événement
                </button>
              </div>
            )}

            {/* Ajouter une photo (organisateur) */}
            {isOwner && (
              <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <p className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>Ajouter une photo</p>
                <div className="flex gap-2">
                  <input type="url" placeholder="URL de la photo (https://...)"
                    value={addPhotoUrl}
                    onChange={e => setAddPhotoUrl(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addPhoto()}
                    className="flex-1 px-3 py-2 rounded-xl text-sm"
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--ink)' }}
                  />
                  <button onClick={addPhoto} disabled={addingPhoto || !addPhotoUrl.trim()}
                    className="btn-teal px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-40">
                    {addingPhoto ? '…' : 'Ajouter'}
                  </button>
                </div>
              </div>
            )}

            {/* Galerie */}
            {photos.length === 0 ? (
              <div className="text-center py-14">
                <Image size={40} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--ink)' }}>Aucune photo pour l'instant</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Ajoutez des photos de votre événement ou utilisez votre photobooth.
                </p>
              </div>
            ) : (
              <>
                {/* Actions */}
                {isOwner && selectedPhotos.length > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-xl"
                    style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                    <p className="text-xs font-semibold flex-1" style={{ color: '#0B4B4E' }}>
                      {selectedPhotos.length} photo(s) sélectionnée(s)
                    </p>
                    <button onClick={() => setShowPrintOrder(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
                      style={{ background: '#0B4B4E', color: '#FFFFFF' }}>
                      <Download size={12} /> Commander impressions
                    </button>
                  </div>
                )}

                {/* Grid photos */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map(ph => (
                    <div key={ph.photo_id} className="relative group rounded-xl overflow-hidden"
                      style={{ aspectRatio: '4/3', border: selectedPhotos.includes(ph.url) ? `2.5px solid ${type.color}` : '1px solid var(--border)' }}>
                      <img src={ph.url} alt={ph.caption || 'Photo'} className="w-full h-full object-cover" loading="lazy" />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-30 transition-opacity" />
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a href={ph.url} download target="_blank" rel="noreferrer"
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ background: 'rgba(255,255,255,0.9)' }}>
                          <Download size={13} style={{ color: 'var(--ink)' }} />
                        </a>
                        {isOwner && (
                          <button onClick={() => deletePhoto(ph.photo_id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center"
                            style={{ background: 'rgba(217,75,93,0.9)' }}>
                            <Trash2 size={13} style={{ color: '#FFFFFF' }} />
                          </button>
                        )}
                      </div>
                      {/* Select for print */}
                      <div className="absolute bottom-2 left-2" onClick={() => {
                        setSelectedPhotos(prev => prev.includes(ph.url)
                          ? prev.filter(u => u !== ph.url)
                          : [...prev, ph.url]);
                      }}>
                        <div className="w-5 h-5 rounded border-2 cursor-pointer"
                          style={{
                            background: selectedPhotos.includes(ph.url) ? type.color : 'rgba(255,255,255,0.8)',
                            borderColor: selectedPhotos.includes(ph.url) ? type.color : 'rgba(255,255,255,0.8)',
                          }}>
                          {selectedPhotos.includes(ph.url) && <Check size={14} style={{ color: '#0B1E20' }} />}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bouton commander toutes */}
                {isOwner && photos.length > 0 && (
                  <button
                    data-testid="btn-commander-impressions"
                    onClick={() => { setSelectedPhotos(photos.map(p => p.url)); setShowPrintOrder(true); }}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
                    style={{ background: 'rgba(212,175,55,0.1)', color: '#C58B19', border: '1px solid rgba(212,175,55,0.25)' }}>
                    <Download size={15} /> Commander des impressions ({photos.length} photo(s))
                  </button>
                )}
              </>
            )}

            {/* Modal commande impression */}
            {showPrintOrder && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
                style={{ background: 'rgba(26,26,46,0.5)', backdropFilter: 'blur(8px)' }}>
                <div className="w-full max-w-lg rounded-3xl overflow-hidden" style={{ background: '#FEFAF5' }}>
                  <div className="flex items-center justify-between p-5 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
                    <div>
                      <p className="font-bold text-base" style={{ color: 'var(--ink)' }}>Commander des impressions</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{selectedPhotos.length} photo(s) sélectionnée(s)</p>
                    </div>
                    <button onClick={() => setShowPrintOrder(false)} className="p-2 rounded-xl" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>✕</button>
                  </div>
                  <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Format</label>
                        <select value={printForm.format} onChange={e => setPrintForm(p => ({ ...p, format: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-sm"
                          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                          <option value="10x15">10×15 cm — 0,50€/photo</option>
                          <option value="13x18">13×18 cm — 0,80€/photo</option>
                          <option value="20x30">20×30 cm — 2,50€/photo</option>
                          <option value="A4">A4 — 3,50€/photo</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Exemplaires</label>
                        <input type="number" min="1" max="100" value={printForm.quantite}
                          onChange={e => setPrintForm(p => ({ ...p, quantite: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-sm"
                          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>Finition</label>
                      <div className="flex gap-2">
                        {['brillant', 'mat'].map(f => (
                          <button key={f} onClick={() => setPrintForm(p => ({ ...p, finish: f }))}
                            className="flex-1 py-2 rounded-xl text-xs font-semibold capitalize"
                            style={{
                              background: printForm.finish === f ? type.color : '#FFFFFF',
                              color: printForm.finish === f ? '#0B1E20' : 'var(--text-muted)',
                              border: `1px solid ${printForm.finish === f ? type.color : 'var(--border)'}`,
                            }}>{f}</button>
                        ))}
                      </div>
                    </div>
                    {[
                      { key: 'livraison_nom', label: 'Nom livraison *', placeholder: 'Jean Dupont' },
                      { key: 'livraison_adresse', label: 'Adresse *', placeholder: '12 Rue de la Fête' },
                      { key: 'livraison_cp', label: 'Code postal *', placeholder: '46100' },
                      { key: 'livraison_ville', label: 'Ville *', placeholder: 'Figeac' },
                      { key: 'email_contact', label: 'Email pour suivi', placeholder: 'vous@email.fr', type: 'email' },
                    ].map(f => (
                      <div key={f.key}>
                        <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                        <input type={f.type || 'text'} placeholder={f.placeholder}
                          value={printForm[f.key]} onChange={e => setPrintForm(p => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-sm"
                          style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }} />
                      </div>
                    ))}
                    <div className="p-3 rounded-xl" style={{ background: 'var(--teal-mist)' }}>
                      <p className="text-xs font-bold" style={{ color: '#0B4B4E' }}>
                        Total estimé : {(({ '10x15': 0.5, '13x18': 0.8, '20x30': 2.5, 'A4': 3.5 }[printForm.format] || 0.5) * parseInt(printForm.quantite || 1) * selectedPhotos.length).toFixed(2)}€
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: 'rgba(11,75,78,0.7)' }}>Traitement et expédition sous 3-5 jours ouvrés</p>
                    </div>
                    <button onClick={submitPrintOrder} disabled={printLoading}
                      className="w-full btn-teal py-3.5 rounded-xl text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2">
                      {printLoading ? <RefreshCw size={15} className="animate-spin" /> : <Download size={15} />}
                      Commander les impressions
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB : PARTENAIRES */}
        {activeTab === 'partenaires' && (
          <div className="space-y-4">
            {partners.length === 0 ? (
              <div className="text-center py-12">
                <Heart size={36} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Partenaires en cours de chargement…</p>
              </div>
            ) : (
              <>
                <div className="p-3 rounded-xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                  <p className="text-xs font-semibold" style={{ color: '#0B4B4E' }}>
                    🤝 Partenaires sélectionnés pour votre {event.event_type_label}
                  </p>
                </div>
                <div className="space-y-3">
                  {partners.map((p, i) => (
                    <div key={p.id} className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: `1px solid ${p.featured ? 'rgba(212,175,55,0.3)' : 'var(--border)'}` }}>
                      {p.featured && (
                        <div className="px-4 py-1.5 flex items-center gap-2" style={{ background: 'rgba(212,175,55,0.08)' }}>
                          <span style={{ fontSize: 12, color: '#D4AF37', fontWeight: 700 }}>★ Partenaire premium</span>
                        </div>
                      )}
                      <div className="flex gap-3 p-4">
                        {p.logo ? (
                          <img src={p.logo} alt={p.nom}
                            className="w-16 h-16 rounded-xl object-contain flex-shrink-0"
                            style={{ background: '#F5F5F5', padding: 4 }} loading="lazy" />
                        ) : (
                          <img src={p.image} alt={p.nom}
                            className="w-16 h-16 rounded-xl object-cover flex-shrink-0" loading="lazy" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{p.nom}</p>
                          <p className="text-xs mb-1.5" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>{p.description}</p>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs" style={{ color: 'var(--teal)' }}>📍 {p.zone}</span>
                            {p.note && <span className="text-xs" style={{ color: '#D4AF37' }}>★ {p.note}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 px-4 pb-4">
                        {p.telephone && (
                          <a href={`tel:${p.telephone.replace(/\s/g, '')}`}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
                            style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                            <Phone size={12} /> {p.telephone}
                          </a>
                        )}
                        {p.site && (
                          <a href={p.site} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
                            style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                            Voir le site
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/partenaires" className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
                  style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
                  Voir tous les partenaires <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        )}

        {/* TAB : DEVIS & PAIEMENTS */}
        {activeTab === 'devis' && (
          <div className="space-y-4">
            {/* Lien configurateur */}
            <div className="p-4 rounded-2xl flex items-center justify-between gap-4"
              style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
              <div>
                <p className="font-semibold text-sm" style={{ color: '#0B4B4E' }}>Créer un devis pour cet événement</p>
                <p className="text-xs" style={{ color: 'rgba(11,75,78,0.7)' }}>Configurateur → devis → signature → paiement</p>
              </div>
              <Link to="/creer-evenement" className="btn-teal px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap flex items-center gap-1.5">
                Configurateur <ArrowRight size={12} />
              </Link>
            </div>

            {/* Alerte acompte non payé */}
            {linkedDevis.some(d => d.statut === 'signe') && (
              <div className="flex items-start gap-3 p-4 rounded-2xl"
                style={{ background: 'rgba(217,75,93,0.08)', border: '1.5px solid rgba(217,75,93,0.3)' }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
                <div>
                  <p className="font-bold text-sm" style={{ color: '#D94B5D' }}>Acompte non payé = date non bloquée</p>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(217,75,93,0.8)', lineHeight: 1.5 }}>
                    Votre devis est signé mais l'acompte n'est pas encore réglé. La date n'est pas réservée tant que l'acompte (30%) n'est pas encaissé.
                  </p>
                  <Link to={`/devis/${linkedDevis.find(d => d.statut === 'signe')?.id}`}
                    className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold"
                    style={{ color: '#D94B5D' }}>
                    Payer l'acompte maintenant <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            )}

            {/* Compteur expiration devis */}
            {linkedDevis.some(d => ['brouillon', 'envoye'].includes(d.statut)) && (() => {
              const devisToSign = linkedDevis.find(d => ['brouillon', 'envoye'].includes(d.statut));
              if (!devisToSign?.created_at) return null;
              const expiresAt = new Date(devisToSign.created_at).getTime() + (10 * 24 * 3600000);
              const daysLeft = Math.ceil((expiresAt - Date.now()) / 86400000);
              if (daysLeft < 0) return null;
              return (
                <div className="flex items-center gap-3 p-3 rounded-xl"
                  style={{ background: daysLeft <= 3 ? 'rgba(197,139,25,0.1)' : 'rgba(125,177,255,0.08)', border: `1px solid ${daysLeft <= 3 ? 'rgba(197,139,25,0.3)' : 'rgba(125,177,255,0.2)'}` }}>
                  <Clock size={14} style={{ color: daysLeft <= 3 ? '#C58B19' : '#7DB1FF', flexShrink: 0 }} />
                  <p className="text-xs font-medium" style={{ color: daysLeft <= 3 ? '#C58B19' : '#7DB1FF' }}>
                    Devis expire dans <strong>{daysLeft} jour{daysLeft > 1 ? 's' : ''}</strong> — Signez pour le valider
                  </p>
                </div>
              );
            })()}

            {/* Devis liés enrichis */}
            {linkedDevis.length > 0 ? linkedDevis.map((q, i) => {
              const statusColors = {
                brouillon: '#CFCFCF', envoye: '#7DB1FF', signe: '#3DDC97',
                acompte_paye: '#D4AF37', solde_paye: '#3DDC97', annule: '#FF5C6C',
              };
              const statusLabels = {
                brouillon: 'Brouillon', envoye: 'Envoyé', signe: 'Signé ✓',
                acompte_paye: 'Acompte payé', solde_paye: 'Soldé ✓', annule: 'Annulé',
              };
              const sc = statusColors[q.statut] || '#CFCFCF';
              return (
                <div key={`mev-item-${i}`} className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Devis {q.numero}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        Créé le {formatDate(q.created_at)}
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold"
                      style={{ background: `${sc}15`, color: sc }}>
                      {statusLabels[q.statut] || q.statut}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 rounded-xl" style={{ background: 'var(--bg)' }}>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Total</p>
                      <p className="price-gold font-bold text-sm">{q.total?.toFixed(0)}€</p>
                    </div>
                    <div className="text-center p-2 rounded-xl" style={{ background: 'var(--bg)' }}>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Acompte</p>
                      <p className="text-sm font-bold" style={{ color: '#5DBCBF' }}>{q.acompte?.toFixed(0)}€</p>
                    </div>
                    <div className="text-center p-2 rounded-xl" style={{ background: 'var(--bg)' }}>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Solde</p>
                      <p className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{q.solde?.toFixed(0)}€</p>
                    </div>
                  </div>
                  <Link to={`/devis/${q.id}`}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold"
                    style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                    Voir le devis <ArrowRight size={13} />
                  </Link>
                </div>
              );
            }) : (
              (!event.devis_links || event.devis_links.length === 0) && (
                <div className="text-center py-12">
                  <FileText size={40} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Aucun devis associé</p>
                </div>
              )
            )}
          </div>
        )}

        {/* TAB : IA ÉVÉNEMENT */}
        {activeTab === 'ia' && (
          <div className="space-y-5">
            {/* Intro */}
            <div className="p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.08), rgba(93,188,191,0.08))', border: '1px solid rgba(139,92,246,0.2)' }}>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles size={20} style={{ color: '#8B5CF6' }} />
                <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>IA Événement — 3 assistants spécialisés</p>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Chaque IA analyse votre événement (type, date, lieu, invités, description) et propose des résultats basés sur votre <strong>catalogue réel</strong>.
                Résultats sauvegardés et régénérables à tout moment.
              </p>
            </div>

            {/* Contexte additionnel */}
            {isOwner && (
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--ink)' }}>
                  Précisions pour l'IA (facultatif)
                </label>
                <textarea
                  rows={2}
                  placeholder="Ex: thème champêtre, budget 2000€, extérieur, ambiance familiale..."
                  value={iaContext}
                  onChange={e => setIaContext(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl text-sm resize-none"
                  style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
                />
              </div>
            )}

            {/* 3 cartes IA */}
            {Object.entries(IA_MODES).map(([mode, cfg]) => (
              <div key={mode} className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: `1px solid ${cfg.color}25` }}>
                {/* Header carte */}
                <div className="flex items-center justify-between p-4"
                  style={{ background: cfg.bg, borderBottom: `1px solid ${cfg.color}15` }}>
                  <div className="flex items-center gap-3">
                    <span style={{ fontSize: 22 }}>{cfg.emoji}</span>
                    <div>
                      <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>IA {cfg.label}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{cfg.desc}</p>
                    </div>
                  </div>
                  {isOwner && (
                    <button
                      data-testid={`ia-btn-${mode}`}
                      onClick={() => runIA(mode)}
                      disabled={!!iaLoading}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold disabled:opacity-50 transition-all"
                      style={{ background: cfg.color, color: '#0B1E20' }}
                    >
                      {iaLoading === mode
                        ? <RefreshCw size={13} className="animate-spin" />
                        : <Zap size={13} />}
                      {iaResults[mode] ? 'Régénérer' : 'Lancer'}
                    </button>
                  )}
                </div>

                {/* Résultat */}
                {iaResults[mode] && (
                  <div className="p-4">
                    <p className="text-xs font-semibold mb-3" style={{ color: cfg.color }}>
                      Résultat généré le {iaResults[mode].generated_at
                        ? formatDate(iaResults[mode].generated_at)
                        : ''}
                    </p>
                    <IAResultRenderer mode={mode} result={iaResults[mode].result} color={cfg.color} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

// ── Renderer résultat IA par mode ────────────────────────────────────────────
function IAResultRenderer({ mode, result, color }) {
  if (!result || typeof result !== 'object') {
    return <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{JSON.stringify(result)}</p>;
  }

  if (mode === 'createur') {
    return (
      <div className="space-y-3">
        {result.theme && <div className="p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)' }}>
          <p className="text-xs font-bold mb-0.5" style={{ color }}>Thème</p>
          <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{result.theme}</p>
          {result.concept && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{result.concept}</p>}
        </div>}
        {result.couleurs?.length > 0 && <div className="flex flex-wrap gap-2">
          {result.couleurs.map((c, i) => <span key={`mev-item-${i}`} className="px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: 'rgba(139,92,246,0.08)', color }}>{c}</span>)}
        </div>}
        {result.idees?.length > 0 && <div>
          <p className="text-xs font-bold mb-2" style={{ color }}>Idées créatives</p>
          <ul className="space-y-1">{result.idees.map((id, i) => <li key={`mev-item-${i}`} className="flex gap-2 text-xs" style={{ color: 'var(--text-muted)' }}><span>✦</span>{id}</li>)}</ul>
        </div>}
        {result.animations?.length > 0 && <div>
          <p className="text-xs font-bold mb-2" style={{ color }}>Animations recommandées</p>
          <ul className="space-y-1">{result.animations.map((a, i) => <li key={`mev-item-${i}`} className="flex gap-2 text-xs" style={{ color: 'var(--text-muted)' }}><span>🎯</span>{a}</li>)}</ul>
        </div>}
        {result.conseil_cle && <div className="p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.06)' }}>
          <p className="text-xs font-bold mb-1" style={{ color }}>Conseil clé</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{result.conseil_cle}</p>
        </div>}
      </div>
    );
  }

  if (mode === 'organisateur') {
    return (
      <div className="space-y-3">
        {result.sections?.map((s, i) => (
          <div key={`list-item-${i}`} className="p-3 rounded-xl" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold" style={{ color: '#0B4B4E' }}>{s.titre}</p>
              {s.delai && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(93,188,191,0.2)', color: '#0B4B4E' }}>{s.delai}</span>}
            </div>
            <ul className="space-y-1">{s.taches?.map((t, j) => <li key={j} className="flex gap-2 text-xs" style={{ color: '#0B4B4E' }}><span>☐</span>{t}</li>)}</ul>
          </div>
        ))}
        {result.points_critiques?.length > 0 && <div>
          <p className="text-xs font-bold mb-2" style={{ color: '#D94B5D' }}>⚠️ Points critiques</p>
          <ul className="space-y-1">{result.points_critiques.map((p, i) => <li key={`list-item-${i}`} className="text-xs" style={{ color: 'var(--text-muted)' }}>• {p}</li>)}</ul>
        </div>}
      </div>
    );
  }

  if (mode === 'planificateur') {
    return (
      <div className="space-y-3">
        {result.planning_avant?.length > 0 && (
          <div>
            <p className="text-xs font-bold mb-2" style={{ color }}>Avant l'événement</p>
            <div className="space-y-1.5">
              {result.planning_avant.map((p, i) => (
                <div key={`mev-item-${i}`} className="flex gap-3 text-xs">
                  <span className="font-bold flex-shrink-0 px-2 py-0.5 rounded" style={{ background: `${color}15`, color }}>{p.delai}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{p.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {result.planning_j?.length > 0 && (
          <div>
            <p className="text-xs font-bold mb-2" style={{ color }}>Jour J — Timeline</p>
            <div className="space-y-1.5">
              {result.planning_j.map((p, i) => (
                <div key={`mev-item-${i}`} className="flex gap-3 text-xs">
                  <span className="font-bold flex-shrink-0 w-12 text-right" style={{ color }}>{p.heure}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{p.etape} {p.duree ? `(${p.duree})` : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {result.timing_cle && (
          <div className="p-3 rounded-xl" style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
            <p className="text-xs font-bold mb-1" style={{ color }}>⏱️ Timing clé</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{result.timing_cle}</p>
          </div>
        )}
      </div>
    );
  }

  // Fallback raw
  return <pre className="text-xs overflow-auto" style={{ color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{JSON.stringify(result, null, 2)}</pre>;
}
